import { useTranslation } from "react-i18next";
import DropdownButtonComponent from "@CubeComponents/form/DropdownButtonComponent";
import { useEffect, useRef, useState } from "react";
import { markReviewMapper, reviewStageMapper } from "./ReviewsConstants";
import css from "./Reviews.module.css";
import DetailsIcon from "@Components/StickySidebarViewer/assets/Details";
import { convertDate } from "../../services/luxonHelper";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import {
  canUserClearReviewLevel,
  getReviewData,
  notifyUserInReview,
  saveUserResponse,
  userPermissionsToMarkResponse,
} from "./ReviewsHelper";
import { showToastNotification } from "../../actions/toast";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../../services/constants";
import { useDispatch, useSelector } from "react-redux";
import ReviewLevelTree from "../SpaceSettings/UserAdministration/Workflows/WorkflowReviewLevels/ReviewLevelTree";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MarkReviewModal from "./MarkReviewModal";
import ReviewLevelIcon from "../../assets/icons/svg/ReviewLevel.svg";
import { getNotificationsCount } from "@Actions/notification";
import { ApproveReviewIcon, SkipReviewIcon } from "../StickySidebarViewer/assets/ReviewIcons";
import ReviewBasicDetails from "./ReviewBasicDetails";
import { fetchReviewData, setReviewData } from "../../actions/Workflows";
import ReviewNotificationModal from "./ReviewNotificationModal";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";

function ReviewProperties({
  closeProperties,
  _id,
  refreshGrid,
  spaceId,
  allowFetchData = true,
  showHeader = true,
  width = "520px",
  accordionOrder = ["details", "reviewFlow"],
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const menuBtnRef = useRef(null);

  const panelStyle = { width, minWidth: width };
  const baseStatus = { status: false, errorMsg: "Permission denied" };
  const defaultReviewNotificationData = {
    show: false,
    action: null,
    data: null,
  };
  const initialState = {
    skip: baseStatus,
    revoke: baseStatus,
    approve: baseStatus,
    rollback: baseStatus,
  };

  const [enableBtns, setEnableBtns] = useState(initialState);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const [canUserClearCurrentLevel, setCanUserClearCurrentLevel] = useState(false);
  const [userCommentModal, setUserCommentModal] = useState({
    show: false,
    type: null,
  });
  const [accordionState, setAccordionState] = useState({
    details: true,
    reviewFlow: true,
  });
  const [reviewNotification, setReviewNotification] = useState(defaultReviewNotificationData);

  const reviewData = useSelector((state) => state.reviewData);

  const menuItems = [
    { id: 1, text: "Close" },
    { id: 2, text: "Revoke", disabled: !enableBtns.revoke.status },
  ];

  const openDetailsOnly = () => {
    setAccordionState({ details: true, reviewFlow: false });
  };

  const openReviewFlowOnly = () => {
    setAccordionState({ details: false, reviewFlow: true });
  };

  const closeReviewNotificationModal = () => {
    setReviewNotification(defaultReviewNotificationData);
  };

  const selectItem = (e) => {
    if (e.item.id === 1 && closeProperties) {
      closeProperties();
    }

    if (e.item.id === 2) {
      initializeUserResponse("revoke");
    }
  };

  const onMenuOpen = (args) => {
    const elem = args.element.parentElement;
    elem.style.left = `${menuBtnRef.current.element.getBoundingClientRect().left - elem.offsetWidth}px`;
  };

  const closeUserCommentModal = () => {
    setUserCommentModal({ show: false, type: null });
  };

  // const notifyUsersInLevelCallback = async (levelId) => {
  //   const response = await notifyUsersInLevel({ levelId, reviewId: _id });

  //   if (response.data.status) {
  //     dispatch(showToastNotification({ content: t("The user(s) have been notified"), type: TOAST_TYPE_SUCCESS }));
  //     dispatch(getNotificationsCount(['NOTIFICATION_UNREAD']));
  //     return;
  //   }

  //   const content = response.data.errorMsg || 'The user(s) could not be notified';
  //   dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
  // };

  const notifyUserInReviewCallback = async (levelId, reviewerId) => {
    const response = await notifyUserInReview({
      levelId,
      reviewId: _id,
      reviewerId,
    });

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The user has been notified"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
      dispatch(fetchReviewData({ reviewId: _id }));
      return;
    }

    const content = response.data.errorMsg || "The user could not be notified";
    dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
  };

  const fetchData = async (reviewId) => {
    const promiseFetchReview = getReviewData({ _id: reviewId });
    const promiseFetchPermissions = userPermissionsToMarkResponse({ reviewId });
    const promiseCanUserClearCurrentLevel = canUserClearReviewLevel({
      reviewId,
    });

    const [fetchReviewResponse, permissionsResponse, canUserClearCurrentLevelResponse] = await Promise.all([
      promiseFetchReview,
      promiseFetchPermissions,
      promiseCanUserClearCurrentLevel,
    ]);

    if (fetchReviewResponse.data.status) {
      dispatch(setReviewData(fetchReviewResponse.data.result));
    }

    if (permissionsResponse.data) {
      const { skip, revoke, approve, rollback } = permissionsResponse.data;
      const data = {
        skip,
        approve,
        revoke,
        rollback,
      };
      setEnableBtns((prev) => ({ ...prev, ...data }));
    }

    if (canUserClearCurrentLevelResponse.data) {
      const value = !!canUserClearCurrentLevelResponse.data.status;
      setCanUserClearCurrentLevel(value);
    }

    setIsLoadingComplete(true);
  };

  const finalizeUserResponse = async (userResponse, userComment) => {
    const { _id: reviewId } = reviewData;
    const response = await saveUserResponse({
      reviewId,
      userResponse,
      userComment,
    });

    setUserCommentModal({ show: false, type: null });

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: markReviewMapper[userResponse].successToast,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      await fetchData(reviewId);

      if (refreshGrid) {
        refreshGrid();
      }

      return;
    }

    dispatch(
      showToastNotification({
        content: response.data.errorMsg || markReviewMapper[userResponse].failureDefaultToast,
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const approveAndClearReviewLevel = async (data) => {
    const { message: userComment, selectedReviewerIdsFromSpace, selectedReviewerIdsAtTargetLevel } = data;
    const userResponse = "approve";
    const approveReviewResponse = await saveUserResponse({
      reviewId: _id,
      userResponse,
      userComment,
      selectedReviewerIdsFromSpace,
      selectedReviewerIdsAtTargetLevel,
    });

    closeReviewNotificationModal();

    if (approveReviewResponse.data.status) {
      dispatch(
        showToastNotification({
          content: "The review has been approved and selected users have been notified",
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      await fetchData(_id);

      if (refreshGrid) {
        refreshGrid();
      }

      return;
    }

    dispatch(
      showToastNotification({
        content: approveReviewResponse.data.errorMsg || markReviewMapper[userResponse].failureDefaultToast,
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const initializeUserResponse = (userResponse) => {
    if (userResponse === "approve" && canUserClearCurrentLevel) {
      const { currentReviewLevel } = reviewData;
      const currentReviewLevelIndex = currentReviewLevel.serial - 1;
      const targetReviewLevel = reviewData.workflow.reviewLevels[currentReviewLevelIndex + 1];

      setReviewNotification({
        show: true,
        action: "clearReviewLevel",
        data: {
          spaceId,
          reviewId: _id,
          modalTitle: "Approve & Notify Users",
          targetLevelReviewers: targetReviewLevel?.reviewers?.filter((record) => !record.isUserDeleted),
          targetLevelSerial: targetReviewLevel?.serial,
        },
      });

      return;
    }

    setUserCommentModal({ show: true, comment: "", type: userResponse });
  };

  const unmountReview = () => {
    dispatch(setReviewData({}));
  };

  useEffect(() => {
    if (allowFetchData) {
      fetchData(_id);
    } else {
      setIsLoadingComplete(true);
    }

    return unmountReview();
  }, [_id]);

  const getUserAvatarComponent = (user) => <UserNameTagComponent id={user.userId} />;

  const detailsHeader = () => (
    <div className={css.accordianHeader}>
      <DetailsIcon size="24" color="#192048" />
      <span>{t("Details")}</span>
    </div>
  );

  const detailsContent = () => (
    <ReviewBasicDetails
      workflowName={reviewData.workflow.name}
      createdBy={reviewData.createdBy}
      currentReviewLevelSerial={reviewData.currentReviewLevel?.serial}
      currentReviewLevelTitle={reviewData.currentReviewLevel?.title}
      nextActionDueDate={reviewData.nextActionDueDate}
      createdDate={reviewData.createdDate}
      finishedDate={reviewData.finishedDate}
      numberOfEntitiesAttached={reviewData.numberOfEntitiesAttached}
    />
  );

  const reviewFlowHeader = () => (
    <div className={css.accordianHeader}>
      <img src={ReviewLevelIcon} />
      <span>{t("Review Flow")}</span>
    </div>
  );

  const reviewFlowContent = () => (
    <div className={css.reviewLevelsDiagram}>
      <ReviewLevelTree
        reviewLevels={reviewData.approvalStages.map((record) => ({
          ...record,
          _id: record.levelId,
        }))}
        showProgress
        showNotifyBtn
        notifyUserInReviewCallback={notifyUserInReviewCallback}
      />
    </div>
  );

  const getDetailsAccordion = () => (
    <Accordion
      disableGutters
      expanded={accordionState.details}
      onChange={() => setAccordionState((prev) => ({ ...prev, details: !prev.details }))}
      className={css.defaultAccordionMUI}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={css.defaultAccordionHeaderMUI}>
        {detailsHeader()}
      </AccordionSummary>
      <AccordionDetails>{detailsContent()}</AccordionDetails>
    </Accordion>
  );

  const getReviewFlowAccordion = () => (
    <Accordion
      disableGutters
      expanded={accordionState.reviewFlow}
      onChange={() => setAccordionState((prev) => ({ ...prev, reviewFlow: !prev.reviewFlow }))}
      className={css.defaultAccordionMUI}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={css.defaultAccordionHeaderMUI}>
        {reviewFlowHeader()}
      </AccordionSummary>
      <AccordionDetails>{reviewFlowContent()}</AccordionDetails>
    </Accordion>
  );

  const getDetailsIcon = () => (
    <DetailsIcon size="24" color="#192048" onClick={openDetailsOnly} className={css.pointer} />
  );

  const getReviewFlowIcon = () => <img src={ReviewLevelIcon} onClick={openReviewFlowOnly} className={css.pointer} />;

  const getAccordionComponent = () => (
    <>
      {accordionOrder.map((record) => {
        const { accordion } = accordionOrderMapper[record];
        return accordion();
      })}
    </>
  );

  const accordionOrderMapper = {
    details: {
      accordion: getDetailsAccordion,
      icon: getDetailsIcon,
    },
    reviewFlow: {
      accordion: getReviewFlowAccordion,
      icon: getReviewFlowIcon,
    },
  };

  const getMarkReviewModal = () => {
    const { approvalStages } = reviewData;
    const currentStage = approvalStages.find((stage) => stage.clearanceStatus === "in_progress");

    if (currentStage) {
      const { requireMandatoryComment } = currentStage;
      const actionMap = { approve: 1, revoke: 2, skip: 3 };
      const userAction = actionMap[userCommentModal.type];

      const isCommentRequired = requireMandatoryComment.enabled && requireMandatoryComment.values.includes(userAction);

      return (
        <MarkReviewModal
          requireMandatoryComment={isCommentRequired}
          closeModal={closeUserCommentModal}
          userResponse={userCommentModal.type}
          finalizeUserResponse={finalizeUserResponse}
        />
      );
    }
  };

  const getReviewNotificationModal = () => {
    const { approvalStages } = reviewData;
    const currentStage = approvalStages.find((stage) => stage.clearanceStatus === "in_progress");

    const requireMandatoryComment = currentStage
      ? currentStage.requireMandatoryComment.enabled && currentStage.requireMandatoryComment.values.includes(1)
      : false;

    return (
      <ReviewNotificationModal
        {...reviewNotification.data}
        action={reviewNotification.action}
        approvalStages={reviewData.approvalStages}
        confirmCallback={approveAndClearReviewLevel}
        cancelCallback={closeReviewNotificationModal}
        requireMandatoryComment={requireMandatoryComment}
      />
    );
  };

  return (
    <>
      {isLoadingComplete && (
        <div className={css.propertiesContainer} style={panelStyle}>
          {userCommentModal.show && getMarkReviewModal()}
          {reviewNotification.show && reviewNotification.action === "clearReviewLevel" && getReviewNotificationModal()}
          {showHeader ? (
            <div className={css.propertiesBasicInfo}>
              <div className={css.reviewSidebarHeaderContainer}>
                <div className={css.reviewIdBox}>RE - {reviewData.serialId}</div>

                <div className={css.flexRowStart}>
                  <div
                    className={`${css.reviewStage} ${css[reviewStageMapper[reviewData.reviewStatus].containerClass]}`}
                  >
                    <div
                      className={`${css.reviewStageIndicator} ${
                        css[reviewStageMapper[reviewData.reviewStatus].indicatorClass]
                      }`}
                    />
                    <p className={css.reviewStatus}>{reviewStageMapper[reviewData.reviewStatus].name}</p>
                  </div>
                  <DropdownButtonComponent
                    select={selectItem}
                    ref={menuBtnRef}
                    open={onMenuOpen}
                    items={menuItems}
                    iconCss="e-more-vertical-1 e-icons"
                    cssClass={`e-caret-hide ${css.ellipsisBackground}`}
                  />
                </div>
              </div>

              {reviewData.currentReviewLevel && (
                <div className={css.reviewSidebarWaitingForContainer}>
                  <div className={css.reviewSidebarWaitingFor}>
                    <DetailsIcon size="24" color="#192048" />
                    {reviewData.currentReviewLevel ? (
                      <p>{`Waiting for reviewer at level ${reviewData.currentReviewLevel.serial}`}</p>
                    ) : (
                      <p>{t("This review is not active")}</p>
                    )}
                  </div>
                </div>
              )}

              <div className={css.reviewDescription}>
                <span>{reviewData.name}</span>
              </div>

              <div className={css.createdDetails}>
                {getUserAvatarComponent(reviewData.createdBy)}
                <span>{t("on")}</span>
                <span className={css.createdDate}>{convertDate(Number(reviewData.createdDate), "dateForText")}</span>
              </div>

              <div className={css.functionalBtns}>
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  onClick={() => initializeUserResponse("approve")}
                  disabled={!enableBtns.approve.status}
                >
                  <div className={css.btnStyle}>
                    <ApproveReviewIcon color={enableBtns.approve.status ? "#192048" : "#13143080"} />
                    Mark As Reviewed
                  </div>
                </ButtonComponent>
                {enableBtns.skip.status && (
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={() => initializeUserResponse("skip")}
                    disabled={!enableBtns.skip.status}
                  >
                    <div className={css.btnStyle}>
                      <SkipReviewIcon />
                      Skip
                    </div>
                  </ButtonComponent>
                )}
              </div>
            </div>
          ) : undefined}
          <div className={css.reviewAccordianContainer}>
            <div className={css.reviewAccordianParent}>{reviewData._id && getAccordionComponent()}</div>
            <div className={css.reviewAccordianSidebar}>
              {accordionOrder.map((record) => {
                const { icon } = accordionOrderMapper[record];
                return icon();
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ReviewProperties;
